import type { FC, ReactNode, RefObject } from 'react'

import { useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { motion, useScroll, useSpring, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { TURN } from '@/lib/hero-timeline'
import TubeRender from '@/components/landing/tube-render'
import Sparkle from '@/components/landing/sparkle'
import DragHint from '@/components/generals/drag-hint'

// WebGL only exists in the browser, so the 3D tube loads on the client. The drawn tube stands in until it is ready.
const TubeScene = dynamic(() => import('@/components/landing/tube-scene'), { ssr: false })

const EASE = [0.22, 1, 0.36, 1] as const

type SceneState = 'loading' | 'ready' | 'failed'

interface HeroTubeProps {
    // The section whose scroll drives the turn.
    sectionRef: RefObject<HTMLElement | null>
    // A card tint utility for the disc behind the tube, for example bg-blush.
    tint: string
    label: string
}

// The same real 3D tube as the home hero: it floats, leans toward the cursor and makes a half turn
// to its back label as the page header scrolls away. Scrolling back up turns it to the front again.
// Visitors can grab anywhere on its square and turn it by hand; it eases back to the front when let go.
const HeroTube: FC<HeroTubeProps> = ({ sectionRef, tint, label }): ReactNode => {
    const startRef = useRef<HTMLDivElement>(null)
    const settleRef = useRef<HTMLDivElement>(null)
    const dragRef = useRef<HTMLDivElement>(null)
    const [scene, setScene] = useState<SceneState>('loading')
    const [grabbed, setGrabbed] = useState(false)

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] })

    // Only the turn part of the shared hero timeline is used here, so no scan band appears.
    const turn = useTransform(scrollYProgress, latest => Math.min(Math.max(latest / 0.7, 0), 1) * TURN[1])
    const progress = useSpring(turn, { stiffness: 140, damping: 32, mass: 1, restDelta: 0.0005 })

    return (
        <div ref={dragRef} className='relative mx-auto aspect-square w-[min(82vw,30rem)]'>
            <div aria-hidden='true' className={cn('absolute inset-[7%] rounded-full', tint)} />

            <Sparkle className='absolute left-[6%] top-[9%] size-4 text-white motion-safe:animate-softly-twinkle' />
            <Sparkle className='absolute bottom-[10%] right-[5%] size-3 text-cocoa/40 motion-safe:animate-softly-twinkle [animation-delay:1.8s]' />

            {scene !== 'failed' && (
                <TubeScene
                    progress={progress}
                    startRef={startRef}
                    settleRef={settleRef}
                    dragRef={dragRef}
                    onReady={() => setScene('ready')}
                    onError={() => setScene('failed')}
                    onGrab={() => setGrabbed(true)}
                    className={cn('z-10 transition-opacity duration-1000 ease-out', scene === 'ready' ? 'opacity-100' : 'opacity-0')}
                />
            )}

            {/* Where the tube stands before scrolling. The 3D scene follows this box. */}
            <div ref={startRef} className='absolute left-1/2 top-[9%] aspect-[0.507] h-[80%] -translate-x-1/2'>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: scene === 'ready' ? 0 : 1, y: 0 }}
                    transition={{ duration: scene === 'ready' ? 0.9 : 1.6, ease: EASE }}
                    className='absolute inset-0'
                >
                    <TubeRender label={label} />
                </motion.div>
            </div>

            {/* Where it settles on its back: a touch smaller, same spot. */}
            <div ref={settleRef} aria-hidden='true' className='absolute left-1/2 top-[15%] aspect-[0.507] h-[68%] -translate-x-1/2' />

            <DragHint show={scene === 'ready' && !grabbed} />
        </div>
    )
}

export default HeroTube
